/**
 * The witness docket (wave W1, coherence review R12, Mahmoud's ruling 6).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The third party inspector's own page: the planned inspection dates on its agency's
 * contracts, from today, nearest first. It used to live only inside the contractor-shaped
 * dashboard the TPI was reading; ruling 6 gives the TPI its own menu, and the docket is
 * its first entry. The server decides which dates the agency may see.
 *
 * Built on the UI foundation (shadcn/ui parts + the TanStack DataTable).
 */

import { Link, useSearchParams } from "react-router";

import { DataTable, type RegisterColumn } from "@/components/ui/data-table";

import { listCalendarActivities, type CalendarActivity } from "../api/calendar";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { AbsencePanel, Breadcrumbs, EmptyState, LoadingLine, StateChip, day } from "./parts";
import { useQuery } from "./useQuery";

/** How far ahead the docket looks unless the link says otherwise (`?days=`). */
export const DOCKET_DAYS = 90;

/** The window a link asks for: a whole number of days from 1 to 366, else the default. */
export function docketDays(param: string | null): number {
  const days = Number(param);
  return Number.isInteger(days) && days >= 1 && days <= 366 ? days : DOCKET_DAYS;
}

function isoDay(offsetDays: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function docketColumns(): RegisterColumn<CalendarActivity>[] {
  return [
    {
      id: "date",
      label: strings.home.columnDate,
      text: (row) => day(row.plannedDate),
      sortValue: (row) => row.plannedDate,
    },
    {
      id: "activity",
      label: strings.home.columnActivity,
      text: (row) => (row.purpose !== null ? `${row.humanId} ${row.purpose}` : row.humanId),
      render: (row) => (
        <Link to={`/activities/${row.id}`} className="q-link q-register__mono">
          {row.humanId}
        </Link>
      ),
    },
    {
      id: "type",
      label: strings.home.columnType,
      text: (row) => row.verificationTypeLabel,
      filter: true,
    },
    {
      id: "contractor",
      label: strings.home.columnContractor,
      text: (row) => row.contractorPartyName,
      filter: true,
    },
    {
      id: "state",
      label: strings.home.columnState,
      text: (row) => row.statusLabel,
      filter: true,
      render: (row) => <StateChip state={row.status} label={row.statusLabel} />,
    },
  ];
}

const COLUMNS = docketColumns();

export function WitnessDocketPage(): React.JSX.Element {
  const token = useToken();
  const [parameters] = useSearchParams();
  // Wave W1 (R13): a tile that counts the next 14 days links here with ?days=14, so the
  // docket lists exactly the dates the tile counted.
  const days = docketDays(parameters.get("days"));
  // Every date the server lets the agency see on its contracts, as the TPI dashboard's
  // docket always read them: the witness is asked to attend, not assigned in advance.
  const feed = useQuery(
    (signal) =>
      listCalendarActivities(token, {
        fromDate: isoDay(0),
        toDate: isoDay(days - 1),
        signal,
      }),
    [token, days],
  );

  return (
    <div data-testid="witness-docket">
      <Breadcrumbs
        trail={[{ label: strings.nav.home, to: "/" }, { label: strings.docket.heading }]}
      />
      <div className="q-ui mb-(--q-s1)">
        <h2 className="q-shell__heading">{strings.docket.heading}</h2>
        <p className="q-shell__lede">{strings.docket.lede}</p>
      </div>
      <section className="q-ui mb-(--q-s2) flex min-w-0 flex-col gap-3">
        <h3 className="text-xs tracking-[0.16em] text-muted-foreground uppercase">
          {strings.docket.horizon(days)}
        </h3>
        {feed.kind === "loading" && <LoadingLine label={strings.common.loading} />}
        {feed.kind === "absence" && <AbsencePanel />}
        {feed.kind === "failed" && (
          <div className="q-refusal" role="alert">
            <p className="q-refusal__heading">{strings.common.failed}</p>
          </div>
        )}
        {feed.kind === "ready" &&
          (feed.data.items.length === 0 ? (
            <EmptyState title={strings.docket.emptyTitle} body={strings.docket.emptyBody} />
          ) : (
            <DataTable
              data={[...feed.data.items].sort((a, b) => a.plannedDate.localeCompare(b.plannedDate))}
              columns={COLUMNS}
              caption={strings.docket.heading}
              testId="docket"
              getRowId={(row) => row.id}
            />
          ))}
      </section>
    </div>
  );
}
